/**
 * `AIBehaviour_Registry.ts` — port of `AI/Registry/AIBehaviour_Registry.cs`.
 *
 * Web slice: `Toggle_Spark` is registered (the Spark head renders via
 * `runtime/assets/sparkBehavior.ts`), and the progressive-disclosure skill
 * tools (`switch_role`, `Load_Skill`) are registered with their executor +
 * `AIInterfaceModel` gating. Pointing-dependent Spark tools (`stand_here`,
 * `stand_wherever_you_want`), wake gesture, guided play mode, recording
 * (`Set_Verbatium`, `save_recording_as_action`), and `wait_for` routing are
 * deferred; they are not registered.
 */

import { getPersonalityIds, getRoleDefinitions, getRoleIds } from "@ai/guardrails/agentPrompts";
import { AgentToolNames } from "./agentToolNames";
import {
  type AIAgentTool,
  boolParam,
  buildParams,
  stringEnum,
  stringList,
  stringParam,
} from "./AIAgentToolRegistryModel";

/**
 * Composes `switch_role`'s description from the LOADED role catalog, so what the
 * model is told about a role and the `/AI/roles/*.md` file that defines it
 * cannot disagree.
 *
 * It replaced a hand-written list of the six shipped roles ("assistant for
 * setup/building/help, guide for leading a walkthrough, …"), which meant the
 * frontmatter `description:` in every role file was dead data and adding or
 * rewording a role left this tool describing the old set — exactly the two-
 * surfaces-that-drift case `Campfire_AI.md`'s prompt rule exists to prevent.
 *
 * Must be RECOMPOSED on every schema emit (see `refreshAIBehaviourDynamicSchema`),
 * not just at registration: tools are registered before `/AI/roles/*.md`
 * finishes loading, and at that moment the catalog is still the legacy bootstrap
 * set, which carries no descriptions.
 */
export function buildSwitchRoleDescription(): string {
  const roles = getRoleDefinitions().map((role) =>
    role.description ? `- ${role.id}: ${role.description}` : `- ${role.id}`,
  );
  return [
    "Switches Spark's role, which changes its persona and the set of skills and tools it can use.",
    "Switch when the user's goal matches a different role.",
    "Available roles:",
    ...roles,
    "Do NOT mention roles to the user.",
  ].join("\n");
}

export function addAIBehaviourTools(tools: AIAgentTool[]): void {
  tools.push(
    {
      name: AgentToolNames.SwitchRole,
      category: "Skills",
      labelWhenActive: "Switching role...",
      description: buildSwitchRoleDescription(),
      humanDescription: "Switch the assistant's role (persona and available skills).",
      parameters: buildParams({
        role: stringEnum("Role to switch to.", false, ...getRoleIds()),
      }),
      enabled: true,
      writerOnly: false,
    },
    {
      name: AgentToolNames.LoadSkill,
      category: "Skills",
      labelWhenActive: "Loading skill...",
      description:
        "Loads an optional skill's full step-by-step guidance by skill_id when the user's request matches a skill listed under 'Available Skills' in the system prompt, or needs a tool that skill carries and you are not currently holding. Call this BEFORE acting on such a request. The tool returns the skill guidance, adds the skill's tools to your tool list, and keeps both active for the rest of the session. Do NOT mention skills, skill ids, or this tool to the user - just apply the guidance naturally. Only pass a skill_id that appears in the Available Skills list.",
      humanDescription: "Load extra task-specific guidance for the assistant.",
      parameters: buildParams({
        skill_id: stringParam(
          "The id of the skill to load, exactly as listed under Available Skills (e.g. 'report-builder').",
          false,
        ),
      }),
      enabled: true,
      writerOnly: false,
    },
    {
      name: AgentToolNames.ChangePersonality,
      category: "AI Behaviour",
      description: `Changes the AI agent personality (voice + visual). Available: ${getPersonalityIds().join(", ")}.`,
      humanDescription: "Change the AI assistant's personality, voice, and appearance.",
      parameters: buildParams({
        personality: stringEnum("Personality to use.", false, ...getPersonalityIds()),
      }),
      enabled: true,
      writerOnly: false,
    },
    {
      name: AgentToolNames.ResetConversation,
      category: "AI Behaviour",
      description:
        "Reconnects the AI session for a fresh conversation while keeping the current project, role, and personality. Use for reset/restart/clear/start over the conversation only.",
      humanDescription: "Start a fresh AI conversation.",
      parameters: buildParams(),
      enabled: true,
      writerOnly: false,
    },
    {
      name: AgentToolNames.Undo,
      category: "AI Behaviour",
      labelWhenActive: "Undoing...",
      description:
        "Triggers Campfire undo and reverts the most recent undoable edit. Use for undo/revert/take that back/go back one step. Not for camera/scene/conversation reset.",
      humanDescription: "Undo the most recent Campfire edit.",
      parameters: buildParams(),
      enabled: true,
      writerOnly: false,
    },
    {
      name: AgentToolNames.UseConstantListening,
      category: "AI Behaviour",
      description:
        "Switches microphone listening mode on every web device, including during workflow Preview and runs. Use enabled=true when the user asks for continuous, constant, always-on, or hands-free listening; the mic stays open with VAD. Use enabled=false to restore the default tap-to-listen mode. Spark confirms the switch with a fixed line of its own, so the call ends your turn: do not speak alongside it.",
      humanDescription:
        "Switch between always-on listening and tap-to-listen.",
      parameters: buildParams({
        enabled: boolParam(
          "True = always-on listening. False = tap-to-listen.",
          undefined,
          false,
        ),
      }),
      enabled: true,
      writerOnly: false,
    },
    {
      name: AgentToolNames.ListenUser,
      category: "AI Behaviour",
      description:
        "Triggers push-to-talk so the user can answer by voice. Call in the SAME response as your question when expecting a voice answer. No-op in constant listening mode.",
      humanDescription: "Open the mic so the user can answer by voice.",
      parameters: buildParams(),
      enabled: true,
      writerOnly: false,
    },
    {
      // Web-first (see `AgentToolNames.DoNothing`). The call ends the turn with
      // nothing spoken (`toolTurnHandover.ts`), because a prompt rule asking
      // for an EMPTY reply loses to the model's default of answering: before
      // this, a chair scrape transcribed as "That's it." drew "Hey Alex! Nice
      // to hear you…" and a side conversation drew the refusal line, 2 of 2
      // each. Its system-prompt half, which OpenAI's voice-prompting guide
      // pairs with the tool, is `base.md`'s `# Silence and Background Audio`:
      // the description alone lost to the base prompt's own rules. Both name
      // categories of audio, never a phrase to avoid saying: a quoted ban is
      // also a phrase in the context window.
      name: AgentToolNames.DoNothing,
      category: "AI Behaviour",
      description:
        "Ends your turn in silence. Call it when the latest audio is not someone speaking to you: silence, noise, a knock or a scrape, music or a TV, people talking to each other, your own voice coming back, or a sound with no words in it. " +
        "A transcript of such audio can still read as words, so judge what you heard, not only the text. " +
        "SILENT CALL: deciding this is your reasoning, not something to say. Call it on its own, with nothing spoken before it and no other tool beside it; it ends your turn, so nothing is said after it either. " +
        "Not for a person talking to you, however short their words are - a one-word answer or command is still for you. If they were talking to you and you could not make out the words, this is not the tool for that. " +
        "Never for a typed message, and never for a line you were asked to say.",
      humanDescription: "Stay silent when the audio was not meant for Spark.",
      parameters: buildParams(),
      enabled: true,
      writerOnly: false,
    },
    {
      name: AgentToolNames.SendUserText,
      category: "Internal",
      description:
        "Injects a text message into the cloud conversation as a user-turn event (equivalent to user text input).",
      humanDescription: "Send a text message into the conversation as if the user typed it.",
      parameters: buildParams({
        text: stringParam("The user text to send to the cloud conversation.", false),
      }),
      enabled: true,
      writerOnly: false,
    },
    {
      name: AgentToolNames.ToggleAIPanel,
      category: "Internal",
      description:
        "Hides or shows the AI panel UI. enabled=true hides it (panel only appears for citations/options - minimal mode). enabled=false keeps the full panel always visible. " +
        `This is the whole panel's MODE - to show or hide just the chat/transcript inside it, use ${AgentToolNames.ToggleTranscript}.`,
      humanDescription:
        "Show the full AI panel or switch to a minimal mode that appears only when needed.",
      parameters: buildParams({
        enabled: boolParam(
          "True to hide the panel (show only for citations/options). False to show the full panel always.",
          undefined,
          false,
        ),
      }),
      enabled: true,
      writerOnly: false,
    },
    /*
      ⚠ Deliberately NOT in `TOOLS_ENABLED_WITHOUT_PROJECT`, unlike the two
      panel toggles either side of it.

      Those set a stored PREFERENCE that applies whenever a project is next
      opened, so they do something with none open. This one commands a LIVE
      panel, and all three of `AiPanel`'s mount sites are inside the project
      editor — so on the projects grid it could only ever answer "no panel".
      A tool that is on the wire and can only refuse is worse than one that is
      withdrawn: the model reaches for it and has to explain itself.
    */
    {
      name: AgentToolNames.ToggleTranscript,
      category: "AI Behaviour",
      labelWhenActive: "Updating the chat...",
      description:
        "Shows or hides the CONVERSATION inside Spark's panel - the chat the user reads: their own " +
        "messages, Spark's replies, and any document links or option buttons under them. They may " +
        "call it the chat, the transcript, the messages or the history. Use for \"show the chat\", " +
        "\"open the transcript\", \"let me see what we said\", \"hide the chat\", \"close that\". It " +
        "works the same during a guided workflow run, where the conversation sits inside the " +
        `workflow panel. NOT ${AgentToolNames.ToggleAIPanel}, which switches the whole panel to a ` +
        `minimal mode, and NOT ${AgentToolNames.ToggleSpark}, which hides the 3D head. Showing it ` +
        "can legitimately do nothing - there are no messages yet, minimal-panel mode is suppressing " +
        "the conversation, or Spark's panel is not on screen - so read the reply and say what it " +
        "reports rather than assuming it worked.",
      humanDescription: "Show or hide the chat transcript in Spark's panel.",
      parameters: buildParams({
        visible: boolParam(
          "True to show the conversation. False to hide it.",
          undefined,
          false,
        ),
      }),
      enabled: true,
      writerOnly: false,
    },
    {
      name: AgentToolNames.ToggleDebugMode,
      category: "Internal",
      description:
        "Enables or disables AI debug mode. When enabled, shows tool calls, reasoning, and other debug info in the conversation; also disables only-last-response and minimal-panel modes for full visibility.",
      humanDescription:
        "Turn AI debug information on or off, including tool calls and reasoning output.",
      parameters: buildParams({
        enabled: boolParam("True to enable debug mode. False to disable.", undefined, false),
      }),
      enabled: true,
      writerOnly: false,
    },
    {
      name: AgentToolNames.ToggleSpark,
      category: "AI Behaviour",
      labelWhenActive: "Updating Spark...",
      description: `Shows or hides Spark's visible 3D head/avatar. visible=true to show, visible=false to hide. Use for hide/show Spark, hide/show Spark's head, make Spark disappear/come back. Do NOT use ${AgentToolNames.ToggleAIPanel} for these - that only changes the panel UI.`,
      humanDescription: "Hide or show Spark's visible head/avatar representation.",
      parameters: buildParams({
        visible: boolParam("True to show. False to hide.", undefined, false),
      }),
      enabled: true,
      writerOnly: false,
    },
    {
      name: AgentToolNames.PresentOptions,
      category: "AI Behaviour",
      description:
        "Renders one or more sequential clickable option rounds in the UI for the user to pick from. " +
        "Use option_sets to declare every round you can see up front; the runtime shows them one at a time and only sends the user's combined picks back to you when the last round is answered. " +
        "Set more_info_required=true if you expect to need another present_options call after the user finishes these rounds before you can act. " +
        "For backward compatibility you may still pass a single prompt + options pair instead of option_sets. " +
        "See the system prompt 'present_options' section for when to use, when to skip, and how to phrase the spoken question. End your response after calling this tool.",
      humanDescription:
        "Show clickable choices when the user needs to pick between concrete matches, interpretations, or next steps.",
      parameters: buildParams({
        option_sets: {
          type: "array",
          minItems: 1,
          maxItems: 4,
          description:
            "Sequential clarification rounds. Each entry is one question with its own set of short user-facing option strings. The user answers one round at a time before the combined picks are sent back as the next user message.",
          items: {
            type: "object",
            properties: {
              question: {
                type: "string",
                description: "Short question shown above this round's buttons.",
              },
              options: {
                type: "array",
                minItems: 2,
                maxItems: 6,
                items: { type: "string" },
                description:
                  "Short user-facing option strings for this round. Each renders as a button.",
              },
            },
            required: ["question", "options"],
          },
        },
        more_info_required: {
          type: "boolean",
          description:
            "Set to true when answering all option_sets is unlikely to be enough information (you expect to call present_options again after the user responds). Defaults to false.",
          default: false,
        },
        prompt: stringParam(
          "Legacy single-round prompt. Prefer option_sets. May mirror the spoken question.",
        ),
        options: stringList(
          "Legacy single-round option strings. Prefer option_sets. Each renders as a button; clicking sends that text as the user's next message.",
        ),
      }),
      enabled: true,
      writerOnly: false,
    },
  );
}
